/* =============================================================================
   CPB MOBILE PREVIEW BUG FIX
   File: cpb-mobile-preview-bug-fix.js

   MOBILE ONLY: 749PX AND BELOW

   PURPOSE
   -----------------------------------------------------------------------------
   Fixes the CPB preview thumbnail temporarily going blank while the user
   scrolls the Shopify product page.

   HOW IT WORKS
   -----------------------------------------------------------------------------
   - Reads the large CPB preview canvases.
   - Composites them into an offscreen canvas.
   - Converts the finished preview to a PNG image.
   - Displays that PNG over the unreliable thumbnail canvas.
   - Hides the original thumbnail canvas.
   - Keeps the last good image frozen while the page is scrolling.
   - Updates the image again after CPB finishes rendering a new preview.

   This file is intentionally separate from:
   cpb-thumbnails-mobile.js
   ============================================================================= */

(function () {

  'use strict';


  /* ==========================================================================
     SETTINGS
     ========================================================================== */

  const MOBILE_MAX_WIDTH = 749;

  const THUMB_SIZE = 160;

  const CHECK_INTERVAL = 30;

  const MAX_WATCH_TIME = 700;

  const SCROLL_RESUME_DELAY = 250;


  /* ==========================================================================
     MOBILE ONLY
     ========================================================================== */

  if (window.innerWidth > MOBILE_MAX_WIDTH) {
    return;
  }


  /* ==========================================================================
     STATE
     ========================================================================== */

  let isScrolling = false;

  let scrollTimer = null;

  let watchTimer = null;

  let watchStartedAt = 0;

  let lastSeenImage = '';

  let stableCount = 0;

  let lastAppliedImage = '';


  /* ==========================================================================
     OFFSCREEN COMPOSITE CANVAS
     ========================================================================== */

  const compositeCanvas =
    document.createElement(
      'canvas'
    );


  compositeCanvas.width =
    THUMB_SIZE;


  compositeCanvas.height =
    THUMB_SIZE;


  const compositeCtx =
    compositeCanvas.getContext(
      '2d'
    );


  if (!compositeCtx) {
    return;
  }


  /* ==========================================================================
     GET PRODUCT BUILDER
     ========================================================================== */

  function getBuilder() {

    return document.querySelector(
      '#product-builder'
    );

  }


  /* ==========================================================================
     GET MAIN CPB PREVIEW CANVASES

     IMPORTANT:
     Thumbnail canvases are excluded.

     We only want the large canvas layers used to construct
     the main product preview.
     ========================================================================== */

  function getMainPreviewCanvases() {

    const builder =
      getBuilder();


    if (!builder) {
      return [];
    }


    return Array.from(
      builder.querySelectorAll(
        'canvas'
      )
    ).filter(
      function (canvas) {

        return (

          !canvas.closest(
            '.cpb-swiper-thumbnails'
          ) &&

          canvas.width > 300 &&

          canvas.height > 300

        );

      }
    );

  }


  /* ==========================================================================
     GET CPB THUMBNAIL CANVAS

     This is the canvas that can disappear during mobile page scrolling.
     ========================================================================== */

  function getThumbnailCanvas() {

    const builder =
      getBuilder();


    if (!builder) {
      return null;
    }


    return builder.querySelector(
      '.cpb-swiper-thumbnails canvas'
    );

  }


  /* ==========================================================================
     GET / CREATE FALLBACK IMAGE

     The fallback image lives inside the same parent as the CPB thumbnail
     canvas.

     The original canvas is hidden and this image becomes the visible
     thumbnail.
     ========================================================================== */

  function getFallbackImage() {

    const thumbnailCanvas =
      getThumbnailCanvas();


    if (!thumbnailCanvas) {
      return null;
    }


    const parent =
      thumbnailCanvas.parentElement;


    if (!parent) {
      return null;
    }


    let img =
      parent.querySelector(
        '.cpb-ios-canvas-fallback'
      );


    if (!img) {

      img =
        document.createElement(
          'img'
        );


      img.className =
        'cpb-ios-canvas-fallback';


      img.alt = '';


      parent.style.position =
        'relative';


      parent.appendChild(
        img
      );

    }


    /*
       Hide CPB's unreliable thumbnail canvas.

       We intentionally use visibility instead of display:none
       so CPB can continue updating and drawing to the canvas.
    */

    thumbnailCanvas.style.setProperty(
      'visibility',
      'hidden',
      'important'
    );


    return img;

  }


  /* ==========================================================================
     BUILD COMPOSITE PREVIEW

     CPB may use several canvases layered on top of one another.

     Draw all large preview canvases into one 160 × 160 offscreen canvas.
     ========================================================================== */

  function buildComposite() {

    const canvases =
      getMainPreviewCanvases();


    if (!canvases.length) {
      return false;
    }


    compositeCtx.clearRect(
      0,
      0,
      THUMB_SIZE,
      THUMB_SIZE
    );


    let successfulLayers = 0;


    canvases.forEach(
      function (canvas) {

        try {

          compositeCtx.drawImage(
            canvas,

            0,
            0,

            canvas.width,
            canvas.height,

            0,
            0,

            THUMB_SIZE,
            THUMB_SIZE
          );


          successfulLayers += 1;

        } catch (error) {

          /*
             A CPB canvas can occasionally be unavailable
             while CPB is rebuilding.

             Ignore that individual layer and allow the
             next check to try again.
          */

        }

      }
    );


    return successfulLayers > 0;

  }


  /* ==========================================================================
     GET CURRENT PREVIEW IMAGE
     ========================================================================== */

  function getCurrentPreviewImage() {

    if (!buildComposite()) {
      return '';
    }


    try {

      return compositeCanvas.toDataURL(
        'image/png'
      );

    } catch (error) {

      console.warn(
        '[CPB Mobile Preview Fix] Snapshot failed:',
        error
      );


      return '';

    }

  }


  /* ==========================================================================
     APPLY FALLBACK IMAGE
     ========================================================================== */

  function applyImage(src) {

    if (
      !src ||
      src === 'data:,'
    ) {

      return;
    }


    const img =
      getFallbackImage();


    if (!img) {
      return;
    }


    /*
       Don't reassign the same data URL unnecessarily.
    */

    if (
      src === lastAppliedImage
    ) {

      /*
         Still make sure the fallback exists and
         the canvas remains hidden.
      */

      return;

    }


    img.src =
      src;


    lastAppliedImage =
      src;

  }


  /* ==========================================================================
     STOP CURRENT PREVIEW WATCH
     ========================================================================== */

  function stopWatching() {

    if (watchTimer) {

      clearInterval(
        watchTimer
      );


      watchTimer =
        null;

    }


    lastSeenImage =
      '';


    stableCount =
      0;

  }


  /* ==========================================================================
     CHECK PREVIEW

     Wait until CPB has produced the same image twice consecutively.

     This prevents capturing an incomplete frame while CPB is still
     rendering customisation layers.
     ========================================================================== */

  function checkPreview() {

    /*
       Never rebuild the fallback while the whole page
       is moving.

       The last known-good image stays visible instead.
    */

    if (isScrolling) {
      return;
    }


    const currentImage =
      getCurrentPreviewImage();


    if (!currentImage) {
      return;
    }


    /*
       Has CPB produced the same frame again?
    */

    if (
      currentImage ===
      lastSeenImage
    ) {

      stableCount += 1;

    } else {

      lastSeenImage =
        currentImage;


      stableCount =
        0;

    }


    /*
       Same frame seen twice.

       Treat it as stable and apply it.
    */

    if (
      stableCount >= 2 &&
      currentImage !==
      lastAppliedImage
    ) {

      applyImage(
        currentImage
      );


      stopWatching();


      return;

    }


    /*
       Safety fallback.

       If CPB never gives us two identical consecutive
       frames, apply the newest valid image after 700ms.
    */

    if (
      performance.now() -
      watchStartedAt >=
      MAX_WATCH_TIME
    ) {

      applyImage(
        currentImage
      );


      stopWatching();

    }

  }


  /* ==========================================================================
     START WATCHING

     Called whenever something inside CPB may have changed the preview.
     ========================================================================== */

  function startWatching() {

    if (isScrolling) {
      return;
    }


    stopWatching();


    watchStartedAt =
      performance.now();


    lastSeenImage =
      '';


    stableCount =
      0;


    /*
       Check immediately.
    */

    checkPreview();


    /*
       Then check every 30ms while CPB finishes rendering.
    */

    watchTimer =
      setInterval(
        checkPreview,
        CHECK_INTERVAL
      );

  }


  /* ==========================================================================
     CHECK WHETHER AN EVENT OCCURRED INSIDE CPB
     ========================================================================== */

  function isInsideCPB(element) {

    return !!(
      element &&
      element.closest &&
      element.closest(
        '#product-builder'
      )
    );

  }


  /* ==========================================================================
     INPUT EVENTS

     Handles:
     - typing
     - backspace
     - delete
     - paste
     - mobile keyboard input
     - text customisation
     ========================================================================== */

  document.addEventListener(
    'input',

    function (event) {

      if (
        !isInsideCPB(
          event.target
        )
      ) {

        return;

      }


      startWatching();

    },

    true
  );


  /* ==========================================================================
     CHANGE EVENTS

     Handles:
     - selects
     - dropdowns
     - finished form changes
     ========================================================================== */

  document.addEventListener(
    'change',

    function (event) {

      if (
        !isInsideCPB(
          event.target
        )
      ) {

        return;

      }


      startWatching();

    },

    true
  );


  /* ==========================================================================
     CLICK EVENTS

     Handles:
     - swatches
     - option buttons
     - thumbnail selections
     - CPB controls
     ========================================================================== */

  document.addEventListener(
    'click',

    function (event) {

      if (
        !isInsideCPB(
          event.target
        )
      ) {

        return;

      }


      /*
         Give CPB a tiny amount of time to begin
         rendering before we start looking for
         the finished preview.
      */

      setTimeout(
        startWatching,
        20
      );

    },

    true
  );


  /* ==========================================================================
     PAGE SCROLL PROTECTION

     The last known-good fallback image is frozen while the page moves.

     After scrolling stops for 250ms, check CPB again for the latest
     stable preview.
     ========================================================================== */

  function markScrolling() {

    isScrolling =
      true;


    /*
       Stop taking snapshots while the page is moving.
    */

    stopWatching();


    clearTimeout(
      scrollTimer
    );


    scrollTimer =
      setTimeout(
        function () {

          isScrolling =
            false;


          /*
             Page has settled.

             Recheck the preview in case CPB changed
             anything while scrolling.
          */

          startWatching();

        },

        SCROLL_RESUME_DELAY
      );

  }


  window.addEventListener(
    'scroll',

    markScrolling,

    {
      passive: true
    }
  );


  window.addEventListener(
    'touchmove',

    markScrolling,

    {
      passive: true
    }
  );


  /* ==========================================================================
     WATCH CPB DYNAMIC DOM

     CPB can create/recreate the thumbnail canvas after page load.

     When a new thumbnail canvas appears, regenerate the fallback.
     ========================================================================== */

  const observer =
    new MutationObserver(
      function (mutations) {

        let foundCanvas =
          false;


        mutations.forEach(
          function (mutation) {

            mutation.addedNodes.forEach(
              function (node) {

                if (
                  !(node instanceof Element)
                ) {

                  return;

                }


                /*
                   The added node itself is the thumbnail canvas.
                */

                if (
                  node.matches(
                    '#product-builder .cpb-swiper-thumbnails canvas'
                  )
                ) {

                  foundCanvas =
                    true;


                  return;

                }


                /*
                   Or the added node contains the thumbnail canvas.
                */

                if (
                  node.querySelector(
                    '#product-builder .cpb-swiper-thumbnails canvas'
                  )
                ) {

                  foundCanvas =
                    true;

                }

              }
            );

          }
        );


        if (foundCanvas) {

          setTimeout(
            startWatching,
            100
          );

        }

      }
    );


  observer.observe(
    document.body,

    {
      childList: true,
      subtree: true
    }
  );


  /* ==========================================================================
     INITIAL LOAD CHECKS

     CPB loads asynchronously, so check several times during startup.
     ========================================================================== */

  window.addEventListener(
    'load',

    function () {

      setTimeout(
        startWatching,
        500
      );


      setTimeout(
        startWatching,
        1000
      );


      setTimeout(
        startWatching,
        2000
      );

    }
  );


  /* ==========================================================================
     IMMEDIATE INITIAL CHECK

     Useful when this asset loads after CPB has already mounted.
     ========================================================================== */

  startWatching();


  console.log(
    '[CPB Mobile Preview Fix] Initialised — max width 749px'
  );

})();